import { safeValue } from '@shared/utils/resource';
import { NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs';
import { OnboardingService } from 'src/app/core/services/onboarding.service';
import { UserService } from 'src/app/core/services/user.service';
import { InitialLoadingService } from '../services/initial-loading.service';

/**
 * Teto absoluto do splash, em milissegundos.
 *
 * Rede de segurança, não caminho normal: qualquer carregamento saudável desliga o splash muito
 * antes disso. Existe porque o splash é `fixed` e cobre a viewport inteira — enquanto ele estiver
 * de pé, nem as telas de erro aparecem, então um caso de falha não previsto deixaria a aplicação
 * inalcançável para sempre (AE-715). Quinze segundos dão folga para rede ruim e ainda assim
 * devolvem a tela em tempo humano.
 */
export const SPLASH_TIMEOUT_MS = 15_000;

/** Rota do onboarding, comparada contra `router.url` — que sempre traz a barra inicial. */
const ONBOARDING_ROUTE = '/onboarding';

@Component({
  selector: 'app-initial-loading',
  imports: [TranslatePipe, NgOptimizedImage],
  templateUrl: './initial-loading.component.html',
  styleUrl: './initial-loading.component\.css',
})
export class InitialLoadingComponent {
  private readonly initialLoadingService = inject(InitialLoadingService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly oidcService = inject(OidcSecurityService);

  protected readonly showLoading = this.initialLoadingService.showLoading;

  /**
   * `isAuthenticated$` emite um objeto (`AuthenticatedResult`), nunca um booleano — ler a
   * emissão inteira como condição dá verdadeiro também para o usuário deslogado.
   */
  private readonly isAuthenticated = toSignal(
    this.oidcService.isAuthenticated$.pipe(
      map((result) => result.isAuthenticated),
    ),
    { initialValue: false },
  );

  /**
   * O carregamento inicial chegou ao fim — com dado ou com erro, tanto faz. O splash existe para
   * cobrir a espera, e espera que terminou mal continua sendo espera que terminou.
   *
   * A ordem dos testes é a própria história das falhas possíveis:
   *
   * 1. Sem resposta do `checkAuth` ainda não dá para julgar nada: os dois desfechos deixam o
   *    token nulo.
   * 2. Sem token, o `/user` nunca é pedido. Não há o que esperar — e as rotas de erro do
   *    blank-shell (a `**` de página não encontrada, em especial) são alcançáveis sem sessão.
   * 3. `/user` em erro encerra o assunto: as tarefas de onboarding dependem dele e ficariam
   *    paradas em Idle. Repare que Idle **não** é estado de erro — a requisição nem chega a ser
   *    disparada, porque a URL não é montada. Era este o splash eterno da AE-715.
   */
  private readonly bootstrapSettled = computed(() => {
    if (!this.userService.authChecked()) return false;
    if (!this.userService.accessToken()) return true;

    const user = this.userService.user;
    if (user.error() !== undefined) return true;
    if (!user.hasValue()) return false;

    const tasks = this.onboardingService.tasks;
    return tasks.error() !== undefined || tasks.hasValue();
  });

  private ceiling: ReturnType<typeof setTimeout> | undefined;

  /** O carregamento inicial acontece uma vez por carga de página, e o desfecho dele também. */
  private settled = false;

  constructor() {
    this.ceiling = setTimeout(() => this.dismiss(), SPLASH_TIMEOUT_MS);
    inject(DestroyRef).onDestroy(() => this.clearCeiling());

    effect(() => {
      if (!this.bootstrapSettled()) return;
      // Uma vez só. O effect continua reagindo ao estado de autenticação — precisa, senão não
      // enxerga a sessão chegar —, e sem esta trava uma renovação de sessão que oscilasse no meio
      // do uso arrastaria o usuário de volta para o onboarding.
      if (this.settled) return;
      this.settled = true;

      // O desligamento espera a navegação para o onboarding porque, sem esperar, o usuário
      // com tarefas pendentes veria o dashboard por um instante antes de ser levado embora.
      // Era o que o antigo `setTimeout(..., 3000)` mascarava, cobrando três segundos de espera
      // de todo mundo — inclusive de quem não ia navegar para lugar nenhum (AE-715).
      void this.redirectToOnboarding().finally(() => this.dismiss());
    });
  }

  /** Leva para o onboarding quando há tarefas pendentes. Sem tarefas, não faz nada. */
  private async redirectToOnboarding(): Promise<void> {
    if (!this.isAuthenticated()) return;

    const tasks = safeValue(this.onboardingService.tasks);
    if (!tasks?.length) return;
    if (this.router.url.startsWith(ONBOARDING_ROUTE)) return;

    await this.router.navigateByUrl(ONBOARDING_ROUTE);
  }

  private dismiss(): void {
    this.clearCeiling();
    this.initialLoadingService.showLoading.set(false);
  }

  private clearCeiling(): void {
    clearTimeout(this.ceiling);
    this.ceiling = undefined;
  }
}
